import { useCallback } from "react";
import { useQueryData, type UseQueryDataResult } from "@src/hooks/useQueryData";
import { boRightsApi } from "./rights/api.core";
import { API_USER_OPERATION_LIST } from "./rights/types";

/** Параметры {@link useBoRights}. */
export interface UseBoRightsOptions {
    /** Код БО. При `null` / `≤0` запрос не выполняется. */
    boCode: number | null | undefined;
    /** Коды прав для проверки. */
    rightCodes: API_USER_OPERATION_LIST[];
    /** Разрешить запрос (по умолчанию `true`). */
    enabled?: boolean;
}

/** Результат {@link useBoRights}. */
export interface UseBoRightsResult {
    /** Функция проверки наличия конкретного права. */
    hasRight: (rightCode: API_USER_OPERATION_LIST) => boolean;
    /** Query-результат проверки прав. */
    query: UseQueryDataResult<Awaited<ReturnType<typeof boRightsApi.check>>>;
}

/**
 * Проверка прав пользователя на БО через отдельный query (не внутри `queryFn` карточки).
 */
export function useBoRights({ boCode, rightCodes, enabled: enabledProp = true }: UseBoRightsOptions): UseBoRightsResult {
    const safeBoCode = boCode != null && boCode > 0 ? boCode : 0;
    const hasValidBoCode = safeBoCode > 0;
    const hasRightsToCheck = rightCodes.length > 0;
    const enabled = enabledProp && hasValidBoCode && hasRightsToCheck;

    const query = useQueryData({
        queryKey: ["boRights", safeBoCode, ...rightCodes.slice().sort((a, b) => a - b)],
        queryFn: () => boRightsApi.check({ boCode: safeBoCode, rightCodes }),
        enabled,
    });

    const hasRight = useCallback(
        (rightCode: API_USER_OPERATION_LIST): boolean => {
            if (!enabled || query.data == null) {
                return false;
            }

            const result = query.data.find((item) => item.RightCode === rightCode);
            return result?.HasRight === true;
        },
        [enabled, query.data],
    );

    return { hasRight, query };
}
